/**
 * THE KEYSTROKE LOGIC OF THE ANSWER FIELD, AS A PURE FUNCTION.
 *
 * This used to live inline in RacePhase's change handler, which is exactly why
 * it shipped broken twice: first a greedy strip ate both O's of "Ooze", then
 * the "fixed" single strip ate the second letter of every word whose second
 * letter repeats the first — type `e`,`l` for "Eel" on an E round and the `e`
 * vanished on the spot, leaving "El" in the box and the player convinced the
 * app was eating their typing. Buried in a handler, nobody could test it.
 * Now it is a function of (letter, typed text) and the tests type real words.
 *
 * ── WHY THE OLD STRIP COULD NEVER WORK ──────────────────────────────────────
 *
 * The strip existed to absorb players re-typing the coral prefix out of habit
 * ("S" for "Sarah" when the S is already printed). But it ran on EVERY
 * keystroke, and at the moment the first `e` lands there is no telling the two
 * intents apart: `e`,`l` is both the genuine remainder of "Eel" AND the start
 * of a re-typed "Elephant". Those are the same keystrokes. Any rule that
 * strips a single leading copy of the letter is GUESSING, and the old guess
 * silently corrupted every Eel, Aardvark, Llama, Ooze and Oomph in the game —
 * corrupted, not flagged: the player found out at the tally.
 *
 * ── WHAT WE DO INSTEAD ──────────────────────────────────────────────────────
 *
 * The field's contract is what every player has learned: the prefix is
 * printed, you type the remainder, and what you type is what you get. So the
 * default is to append VERBATIM. We keep exactly one absorbing rule, the one
 * case that is unambiguous: text that opens with the letter DOUBLED
 * ("ee…" on an E round) can only be a re-typed prefix on a double-letter word,
 * because the prefix would make it a triple — and no answer starts with three
 * of the same letter. So `e`,`e`,`l` (the habit path) also lands "Eel", while
 * `e`,`l` (the learned path) is never touched.
 *
 * A habit typist writing a NON-double word ("elephant" in full) now sees
 * "E|elephant" — visibly doubled at the front of the box, one backspace to
 * fix. That is the trade, and it is the right one: a visible mistake the
 * player made beats a silent one the app made. (The whole-word toggle exists
 * for anyone who wants their habit honoured — no prefix, no rules.)
 *
 * Paste is the one gesture where a single leading copy IS unambiguous: nobody
 * pastes a remainder. A pasted "Elephant" or "Eel" sheds one leading copy and
 * lands whole.
 */

/** True when `ch` is `letter`, either case. Empty/undefined is false. */
const isLetter = (letter: string, ch: string | undefined): boolean =>
  !!ch && ch.toUpperCase() === letter.toUpperCase();

/**
 * Prefix mode: the stored answer for what the input now shows after the
 * coral prefix. Returns the FULL word (prefix included) or '' for an empty
 * box — the same shape the server grades.
 *
 * Do NOT upper-case what the player typed. The prefix IS the capital: the
 * field renders <span>O</span> + <input>scar</input>, so force-capitalising
 * the first typed character produced "OScar", "ORlando", "PEncil" — every
 * word in the playtest came out visibly broken. `letter + typed` gives a
 * correctly-cased "Oscar".
 */
export function composePrefixed(
  letter: string,
  typed: string,
  opts: { pasted?: boolean } = {},
): string {
  if (!letter) return typed;
  let rest = typed;

  if (opts.pasted) {
    // A paste is a whole word, so one leading copy of the letter is the
    // word's own first letter, already printed as the prefix. Shed it once —
    // "Eel" sheds to "el" and re-attaches whole; "lephant" is left alone.
    if (isLetter(letter, rest[0])) rest = rest.slice(1);
  }

  // The doubled-leading-letter collapse described above. A loop, not a single
  // pass: absorbing one copy must never leave another doubled pair in front,
  // or mashing the key would let "eee…" through as a triple.
  while (isLetter(letter, rest[0]) && isLetter(letter, rest[1])) {
    rest = rest.slice(1);
  }

  return rest ? `${letter}${rest}` : '';
}

/**
 * Whole-word mode: no prefix on screen, the player types the entire word.
 * Stored verbatim except the first character is capitalised — here that IS
 * the word's first letter (unlike prefix mode, where capitalising the first
 * typed char was the "OScar" bug), and it is what the prefix used to do for
 * them: "eel" → "Eel", exactly what the tally prints.
 */
export function composeWhole(typed: string): string {
  const i = typed.search(/\S/);
  if (i < 0) return '';
  return typed.slice(0, i) + typed[i].toUpperCase() + typed.slice(i + 1);
}

/**
 * Does this answer open with the round's letter? Empty boxes pass — an empty
 * box is a choice, not a mistake. Only whole-word mode can fail this (prefix
 * mode manufactures compliance); the server's validator enforces the same
 * rule, so this is the courtesy warning, not the referee.
 */
export function startsWithRoundLetter(letter: string, answer: string): boolean {
  const w = answer.trim();
  if (!w) return true;
  return isLetter(letter, w[0]);
}
